import { Clock, Dices, Play, Sofa, Users, Zap } from 'lucide-react';
import { useRef } from 'react';
import { Modal } from '../../components/Modal';
import { Pill } from '../../components/ui/Pill';
import { getMainCategory } from '../../domain/activities';
import type { Suggestion } from '../../domain/suggest';
import type { Activity } from '../../domain/types';
import { MAIN_CATEGORY_THEMES } from '../../utils/categoryThemes';
import { ReasonPills } from './reasons';

interface SurpriseModalProps {
  suggestion: Suggestion;
  onStart: (activity: Activity) => void;
  onAnother: () => void;
  onClose: () => void;
}

const tonalBtn =
  'inline-flex min-h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-black/5 px-4 text-sm font-semibold text-slate-900 transition-all duration-150 active:scale-95 hover:bg-black/10 dark:border dark:border-white/10 dark:bg-slate-800/80 dark:text-slate-100 dark:hover:bg-slate-700/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:focus-visible:outline-slate-100';

export function SurpriseModal({ suggestion, onStart, onAnother, onClose }: SurpriseModalProps) {
  const startRef = useRef<HTMLButtonElement>(null);
  const a = suggestion.activity;
  const theme = MAIN_CATEGORY_THEMES[getMainCategory(a.type, a.energie, a.categorie)];
  const CategoryIcon = theme.icon;
  const duur = Math.max(0, Math.round(Number(a.duur) || 0));

  return (
    <Modal title="Dit gaan we doen!" size="sm" onClose={onClose} initialFocusRef={startRef}>
      <div className={`rounded-3xl p-5 ${theme.cardBg} ${theme.textColor}`} data-testid="surprise-result">
        <Pill icon={CategoryIcon} className={`${theme.pillBg} ${theme.accent}`}>
          {theme.label}
        </Pill>
        <h3 className="mt-3 text-xl font-bold leading-snug" data-testid="surprise-name">
          {a.naam}
        </h3>
        {a.omschrijving ? <p className="mt-1 text-sm leading-relaxed opacity-85">{a.omschrijving}</p> : null}
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Pill icon={Clock} className={theme.timerBoxBg}>
            {duur} min
          </Pill>
          {a.aantalPersonen ? (
            <Pill icon={Users} className={theme.timerBoxBg}>
              {a.aantalPersonen} pers.
            </Pill>
          ) : null}
          {a.energie ? (
            <Pill icon={a.energie === 'Veel energie' ? Zap : Sofa} className={theme.timerBoxBg}>
              {a.energie}
            </Pill>
          ) : null}
        </div>
        {suggestion.reasons.length > 0 ? (
          <div className="mt-3">
            <ReasonPills reasons={suggestion.reasons} className={`${theme.pillBg} ${theme.accent}`} />
          </div>
        ) : null}
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <button
          ref={startRef}
          type="button"
          autoFocus
          onClick={() => onStart(a)}
          className={`inline-flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-5 text-base font-bold transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current ${theme.buttonBg}`}
        >
          <Play size={18} strokeWidth={2.4} aria-hidden="true" className="fill-current" />
          Start
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onAnother} className={tonalBtn}>
            <Dices size={16} aria-hidden="true" /> Nog een
          </button>
          <button type="button" onClick={onClose} className={tonalBtn}>
            Sluiten
          </button>
        </div>
      </div>
    </Modal>
  );
}
